import React, { useState } from "react";
import { INVOICE_EMAIL_VARIABLES } from "@common/helpers/invoice-email-variables";
import { Field } from "@design-system/Field/Field";
import { Input } from "@design-system/Input/Input";
import { Switch } from "@design-system/Switch/Switch";
import { TextArea } from "@design-system/TextArea/TextArea";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { VariableTextField } from "@components/forms/VariableTextField/VariableTextField";
import { ScheduleSendAtField } from "./ScheduleSendAtField";
import { EmailRecipientsField } from "./EmailRecipientsField";
import { ReplyToEmailField } from "./ReplyToEmailField";
import {
    isDetailSourcedRecipient,
    retainLockedRecipients,
} from "./nativeInvoiceFormHelpers";
import { useNativeInvoiceFormContext } from "./NativeInvoiceFormContext";
import { useNativeInvoiceSenderAddress } from "./useNativeInvoiceSender";
import styles from "./SidePanel.module.scss";

// The email form fields, split from EmailSection's card chrome so the same
// fields can also render flat inside the template flow's Email accordion.

export const SendEmailRecipientFields: React.FC = () => {
    const { state, setState } = useNativeInvoiceFormContext();

    // CC/BCC are rarely used, so both rows stay hidden behind an "Add CC &
    // BCC" action unless the draft already carries recipients in either
    // (same pattern as the Items step's tax/discount/deposit rows).
    const [areCcBccRevealed, setAreCcBccRevealed] = useState(false);
    const shouldShowCcBcc =
        areCcBccRevealed ||
        state.emailCc.trim() !== "" ||
        state.emailBcc.trim() !== "";

    return (
        <>
            <ReplyToEmailField />
            <EmailRecipientsField
                label="To"
                value={state.emailTo}
                onChange={(value) =>
                    setState((prev) => ({
                        ...prev,
                        emailTo: value,
                    }))
                }
            />
            {shouldShowCcBcc ? (
                <>
                    <EmailRecipientsField
                        label="CC"
                        value={state.emailCc}
                        isValueRemovable={(email) =>
                            !isDetailSourcedRecipient(email, state.customer?.cc)
                        }
                        onChange={(value) =>
                            setState((prev) => ({
                                ...prev,
                                emailCc: retainLockedRecipients({
                                    current: prev.emailCc,
                                    next: value,
                                    lockedSource: prev.customer?.cc,
                                }),
                            }))
                        }
                    />
                    <EmailRecipientsField
                        label="BCC"
                        value={state.emailBcc}
                        isValueRemovable={(email) =>
                            !isDetailSourcedRecipient(
                                email,
                                state.customer?.bcc,
                            )
                        }
                        onChange={(value) =>
                            setState((prev) => ({
                                ...prev,
                                emailBcc: retainLockedRecipients({
                                    current: prev.emailBcc,
                                    next: value,
                                    lockedSource: prev.customer?.bcc,
                                }),
                            }))
                        }
                    />
                </>
            ) : (
                <button
                    type="button"
                    className={styles.addFieldsRow}
                    onClick={() => setAreCcBccRevealed(true)}
                >
                    Add CC &amp; BCC
                </button>
            )}
        </>
    );
};

export const SendEmailFields: React.FC = () => {
    const { state } = useNativeInvoiceFormContext();
    const senderAddress = useNativeInvoiceSenderAddress(state.sendingEmailId);
    // The email's From header the way the recipient will see it: the sending
    // business plus the address the message actually goes out from. Read-only
    // here — the sender is chosen on the From entity picker in Details.
    const fromDisplay = state.from.businessName
        ? `${state.from.businessName} <${senderAddress}>`
        : senderAddress;

    return (
        <>
            <Field label="From">
                {/* The tooltip anchors to the input alone, so it pops next to
                    the control. */}
                <Tooltip content="To change the sender, edit From on the previous step">
                    <Input size="xl" value={fromDisplay} disabled />
                </Tooltip>
            </Field>
            <SendEmailRecipientFields />
        </>
    );
};

interface EmailDetailsFieldsProps {
    isTemplateMode?: boolean;
}

export const EmailDetailsFields: React.FC<EmailDetailsFieldsProps> = ({
    isTemplateMode = false,
}) => {
    const { state, setState } = useNativeInvoiceFormContext();

    const subjectField = isTemplateMode ? (
        <VariableTextField
            value={state.emailSubject}
            size="sm"
            label="Subject"
            placeholder="Add subject line"
            variables={INVOICE_EMAIL_VARIABLES}
            onChange={(value) =>
                setState((prev) => ({
                    ...prev,
                    emailSubject: value,
                }))
            }
        />
    ) : (
        <Field label="Subject">
            <Input
                size="xl"
                maxLength={255}
                placeholder="Add subject line"
                value={state.emailSubject}
                onChange={(event) =>
                    setState((prev) => ({
                        ...prev,
                        emailSubject: event.target.value,
                    }))
                }
            />
        </Field>
    );

    const bodyField = isTemplateMode ? (
        <VariableTextField
            value={state.emailMessage}
            size="xxs"
            label="Body"
            placeholder="Add a message to your customer"
            variables={INVOICE_EMAIL_VARIABLES}
            lineBreaks="multi"
            onChange={(value) =>
                setState((prev) => ({
                    ...prev,
                    emailMessage: value,
                }))
            }
        />
    ) : (
        <Field label="Body">
            <TextArea
                size="xl"
                autoGrow
                minRows={4}
                maxRows={11}
                value={state.emailMessage}
                onChange={(event) =>
                    setState((prev) => ({
                        ...prev,
                        emailMessage: event.target.value,
                    }))
                }
                placeholder="Add a message to your customer"
                maxLength={5000}
            />
        </Field>
    );

    return (
        <>
            {subjectField}
            {bodyField}
            <Switch
                checked={state.emailAttachPdf}
                onCheckedChange={(value) =>
                    setState((prev) => ({
                        ...prev,
                        emailAttachPdf: value,
                    }))
                }
                label={
                    <span className={styles.toggleRowLabel}>Attach PDF</span>
                }
                labelPlacement="start"
                className={styles.sectionTitleToggleRow}
            />
        </>
    );
};

interface ScheduleEmailFieldsProps {
    switchClassName?: string;
    bodyClassName?: string;
}

export const ScheduleEmailFields: React.FC<ScheduleEmailFieldsProps> = ({
    switchClassName,
    bodyClassName,
}) => {
    const { state, setState } = useNativeInvoiceFormContext();

    return (
        <>
            <Switch
                checked={state.scheduleEnabled}
                onCheckedChange={(value) =>
                    setState((prev) => ({
                        ...prev,
                        scheduleEnabled: value,
                    }))
                }
                label={<span className={styles.toggleRowLabel}>Schedule</span>}
                labelPlacement="start"
                className={switchClassName}
            />
            {state.scheduleEnabled && (
                <div className={bodyClassName}>
                    <Field label="Send at" control="button">
                        <ScheduleSendAtField
                            value={state.scheduledSendAt}
                            onChange={(value) =>
                                setState((prev) => ({
                                    ...prev,
                                    scheduledSendAt: value,
                                }))
                            }
                        />
                    </Field>
                </div>
            )}
        </>
    );
};
